import { Checkbox, Grid, GridColumn, GridRow, RadioGroup } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { Form, Formik } from 'formik';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { showSuccessSaveToast, showUserDbErrorToast } from 'utils/toasts';
import { GroupVisibility } from '../../../client/gql/graphql';
import { OrganizationConfigContext } from '../../../context/OrganizationConfigContext';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';

const UpdateCourseSettingsMutationDocument = graphql(`
  mutation UpdateCourseSettingsFields($where: GroupWhere!, $update: UpdateGroupSettings!) {
    updateCourseSettings: updateGroupSettings(where: $where, update: $update) {
      uuid
      __typename
      visibility
      showLessonEventsInClassbook
      showImportedLessonEvents
      nextcloudConnection {
        __typename
        edges {
          properties {
            enabled
            lastSync
          }
        }
      }
      threemaBroadcastConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            enabled
            lastSync
          }
        }
      }
    }
  }
`);

const CourseSettingsFormPageQueryDocument = graphql(`
  query CourseSettingsFormPageData($courseUuid: ID!) {
    courses: groups(where: { uuid: $courseUuid }) {
      uuid
      __typename
      visibility
      showLessonEventsInClassbook
      showImportedLessonEvents
      nextcloudConnection {
        __typename
        edges {
          properties {
            __typename
            enabled
          }
        }
      }
      threemaBroadcastConnection {
        __typename
        edges {
          properties {
            __typename
            enabled
          }
        }
      }
    }
  }
`);

interface CourseSettingsProps {
  courseUuid: string;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
}

type CourseSettingsFormType = {
  nextcloudFolder: boolean;
  threemaGroup: boolean;
  visibility?: GroupVisibility | null;
  showLessonEventsInClassbook?: boolean;
  showImportedLessonEvents?: boolean;
};

export const CourseSettingsForm = ({ courseUuid, onLoad, onClose }: CourseSettingsProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organization = pimAuthClaims.getOrganization();

  const [{ data }] = useQuery({
    query: CourseSettingsFormPageQueryDocument,
    variables: {
      courseUuid: courseUuid,
    },
    pause: !courseUuid,
    requestPolicy: 'network-only',
  });

  const perms = usePermissionChecker();

  const { threemaBroadcastLicense, cloudLicense } = useContext(OrganizationConfigContext);

  const [, updateCourseSettings] = useMutation(UpdateCourseSettingsMutationDocument);

  const handleSubmit = async (values: CourseSettingsFormType) => {
    if (!perms?.canUpdateGroupSettings({ uuid: courseUuid, organization: { uuid: organization.uuid } })) return;

    onLoad(true);

    const { error } = await updateCourseSettings({
      where: { uuid: courseUuid },
      update: {
        nextcloudFolder: values.nextcloudFolder,
        threemaGroup: values.threemaGroup,
        visibility: values.visibility,
        showImportedLessonEvents: values.showImportedLessonEvents,
        showLessonEventsInClassbook: values.showLessonEventsInClassbook,
      },
    });

    error ? showUserDbErrorToast() : showSuccessSaveToast();

    onLoad(false);
    onClose();
  };

  const initialValues: CourseSettingsFormType = {
    nextcloudFolder: data?.courses[0]?.nextcloudConnection.edges[0]?.properties.enabled ?? false,
    threemaGroup: data?.courses[0]?.threemaBroadcastConnection.edges[0]?.properties.enabled ?? false,
    visibility: data?.courses[0]?.visibility,
    showImportedLessonEvents: data?.courses[0]?.showImportedLessonEvents ?? true,
    showLessonEventsInClassbook: data?.courses[0]?.showLessonEventsInClassbook ?? true,
  };

  return (
    <Formik onSubmit={handleSubmit} initialValues={initialValues}>
      {({ isSubmitting, errors, setFieldValue, values }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow headline={t('common.visibility')}>
                <GridColumn width={12}>
                  <RadioGroup
                    options={[
                      { label: t('courses.settingsForm.visibleEverywhere'), value: 'full' },
                      { label: t('courses.settingsForm.invisible'), value: 'hidden' },
                    ]}
                    value={values.visibility ? values.visibility : 'full'}
                    name={'visibility'}
                    onChange={(value) => setFieldValue('visibility', value)}
                  />
                </GridColumn>
              </GridRow>
              <GridRow>
                <GridColumn width={12}>
                  <Checkbox
                    label={t('courses.settingsForm.showImported')}
                    checked={values.showImportedLessonEvents}
                    name='showImportedLessonEvents'
                    onChange={() => setFieldValue('showImportedLessonEvents', !values['showImportedLessonEvents'])}
                  />
                  <Checkbox
                    label={t('courses.settingsForm.showInClassbook')}
                    checked={values.showLessonEventsInClassbook}
                    name='showLessonEventsInClassbook'
                    onChange={() =>
                      setFieldValue('showLessonEventsInClassbook', !values['showLessonEventsInClassbook'])
                    }
                  />
                </GridColumn>
              </GridRow>

              <GridRow headline={t('common.cooperation')}>
                <GridColumn width={6}>
                  <Checkbox
                    label={t('groups.asCloudDirectory')}
                    checked={values.nextcloudFolder}
                    name='nextcloudFolder'
                    onChange={() => setFieldValue('nextcloudFolder', !values['nextcloudFolder'])}
                    disabled={!cloudLicense}
                  />
                </GridColumn>
                <GridColumn width={6}>
                  <Checkbox
                    label={t('groups.asGroupInThreema')}
                    checked={values.threemaGroup}
                    name='threemaGroup'
                    onChange={() => setFieldValue('threemaGroup', !values['threemaGroup'])}
                    disabled={!threemaBroadcastLicense}
                  />
                </GridColumn>
              </GridRow>
            </Grid>
            <ModalBottomButtons closeButton={{ callback: onClose }} isLoading={isSubmitting} errors={errors} />
          </Form>
        );
      }}
    </Formik>
  );
};
